import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, startViewTransition } from '@theme/utilities';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';
/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  /**
   * Creates URL parameters
   * @param {FormData} [overridingFormData] - Optional form data to override or complement the current state
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(overridingFormData) {
    const formData = new FormData();
    
    // Collect all named inputs within the entire component to ensure cumulative filtering
    // regardless of whether they are in the same form or multiple forms.
    const allInputs = this.querySelectorAll('input[name], select[name]');
    allInputs.forEach(input => {
      if (input.disabled) return;
      if (input.type === 'checkbox' || input.type === 'radio') {
        if (input.checked) formData.append(input.name, input.value);
      } else if (input.value !== '') {
        // Skip appending default Min boundary
        if (input.name === 'filter.v.price.gte' && parseInt(input.value) === 0) {
           return;
        }
        // Skip appending default Max boundary
        if (input.name === 'filter.v.price.lte') {
           const maxAttr = input.getAttribute('max');
           if (maxAttr && parseInt(input.value) === parseInt(maxAttr)) return;
        }
        
        formData.append(input.name, input.value);
      }
    });

    // Merge overriding form data if provided
    if (overridingFormData) {
      for (const [key, value] of overridingFormData.entries()) {
        formData.set(key, value);
      }
    }

    let newParameters = new URLSearchParams(/** @type any */ (formData));

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    newParameters.delete('page');

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    // Remove sorting if it's the default
    const sortingFilter = this.querySelector('sorting-filter-component');
    const defaultSortBy = sortingFilter?.querySelector('[data-default-sort-by]')?.dataset.defaultSortBy;
    if (newParameters.get('sort_by') === defaultSortBy) {
      newParameters.delete('sort_by');
    }

    return newParameters;
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  /**
   * Updates filters and renders the section
   */
  updateFilters = debounce(() => {
    this.#updateURLHash();
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  }, 400);

  /**
   * Updates the section
   */
  connectedCallback() {
     this.#initFiltersPersistence();
  }

  /**
   * Initializes the filter persistence. 
   * If we don't have a cached master set for this page, we fetch it in the background.
   */
  async #initFiltersPersistence() {
    const key = window.location.pathname;
    window._facets_full_filters = window._facets_full_filters || {};

    if (window._facets_full_filters[key]) return;

    // If we're on a clean page, cache immediately
    if (window.location.search === '' || window.location.search === '?q=') {
      this.#cacheOriginalFullFilterSet();
    } else {
      // We are on a filtered page. Fetch the master structure in the background.
      try {
        const masterUrl = new URL(window.location.origin + window.location.pathname);
        masterUrl.searchParams.set('section_id', this.sectionId);
        
        const response = await fetch(masterUrl.toString());
        const text = await response.text();
        const parser = new DOMParser();
        const htmlDoc = parser.parseFromString(text, 'text/html');
        
        window._facets_full_filters[key] = {};
        ['drawer', '', 'top'].forEach(suffix => {
          const id = suffix ? `#facets-filters-wrapper-${suffix}-${this.sectionId}` : `#facets-filters-wrapper-${this.sectionId}`;
          const wrapper = htmlDoc.querySelector(id);
          if (wrapper) {
            window._facets_full_filters[key][suffix] = wrapper.innerHTML;
          }
        });

        // After fetching master, restore any categories currently hidden
        this.#restoreMissingFiltersFromCache();
        const urlObj = new URL(window.location.href);
        this.#syncInputs(urlObj.searchParams);
      } catch (e) {
        console.warn('Failed to fetch master filters:', e);
      }
    }
  }

  /**
   * Caches the original full set of filters from the current DOM.
   */
  #cacheOriginalFullFilterSet() {
    const key = window.location.pathname;
    window._facets_full_filters = window._facets_full_filters || {};
    window._facets_full_filters[key] = window._facets_full_filters[key] || {};

    ['drawer', '', 'top'].forEach(suffix => {
      const id = suffix ? `#facets-filters-wrapper-${suffix}-${this.sectionId}` : `#facets-filters-wrapper-${this.sectionId}`;
      const wrapper = document.querySelector(id);
      if (wrapper) {
          window._facets_full_filters[key][suffix] = wrapper.innerHTML;
      }
    });
  }

  /**
   * Restores missing filters from the cache after an update.
   */
  #restoreMissingFiltersFromCache() {
    const key = window.location.pathname;
    if (!window._facets_full_filters || !window._facets_full_filters[key]) return;

    // Restore entire wrappers to freeze filter options
    ['drawer', '', 'top'].forEach(suffix => {
      const id = suffix ? `#facets-filters-wrapper-${suffix}-${this.sectionId}` : `#facets-filters-wrapper-${this.sectionId}`;
      const currentWrapper = document.querySelector(id);
      const cachedHtml = window._facets_full_filters[key][suffix];
      
      if (currentWrapper && cachedHtml) {
         currentWrapper.innerHTML = cachedHtml;
      }
    });

    // Fire events for custom rich components to re-sync their UI
    setTimeout(() => {
      document.querySelectorAll('price-facet-rich-component').forEach(el => {
        if (typeof el.initFromInputs === 'function') el.initFromInputs();
      });
      document.querySelectorAll('year-facet-rich-component').forEach(el => {
        if (typeof el.initFromCheckboxes === 'function') el.initFromCheckboxes();
      });
    }, 50);
  }

  /**
   * Updates the section
   */
  #updateSection() {
    const viewTransition = !this.closest('dialog');

    const handleUpdate = () => {
       sectionRenderer.renderSection(this.sectionId).then(() => {
          this.#restoreMissingFiltersFromCache();
          const urlObj = new URL(window.location.href);
          this.#syncInputs(urlObj.searchParams);
       });
    };

    if (viewTransition) {
      try {
        startViewTransition(handleUpdate, ['product-grid']);
      } catch (e) {
        handleUpdate();
      }
    } else {
      handleUpdate();
    }
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  updateFiltersByURL(url) {
    history.pushState('', '', url);
    const urlObj = new URL(url, window.location.origin);
    this.#updateSection();
  }

  /**
   * Syncs the static DOM inputs with the requested URL params (for clear all and chips)
   */
  #syncInputs(searchParams) {
    const checkboxes = this.querySelectorAll('input[type="checkbox"]');
    checkboxes.forEach(input => {
      const activeValues = searchParams.getAll(input.name);
      input.checked = activeValues.includes(input.value);
    });

    const priceInputs = this.querySelectorAll('input[type="number"], input[type="range"]');
    priceInputs.forEach(input => {
      if (!input.name) return;
      const val = searchParams.get(input.name);
      if (val !== null) {
          input.value = val;
      } else {
          // If the filter is totally cleared, reset to their maximum bounds
          if (input.className.includes('price-input-min')) {
              input.value = 0;
          } else if (input.className.includes('price-input-max')) {
              const maxAttr = input.getAttribute('max') || input.parentElement?.parentElement?.parentElement?.querySelector('.range-max')?.max;
              if (maxAttr) input.value = maxAttr;
          } else if (input.className.includes('year-input-min')) {
              input.value = input.getAttribute('min') || input.getAttribute('placeholder') || '';
          } else if (input.className.includes('year-input-max')) {
              input.value = input.getAttribute('max') || input.getAttribute('placeholder') || '';
          } else if (input.type === 'range' && input.className.includes('range-min')) {
              input.value = input.min;
          } else if (input.type === 'range' && input.className.includes('range-max')) {
              input.value = input.max;
          }
      }
      input.dispatchEvent(new Event('input', { bubbles: true }));
    });

    const defaultSortBy = this.querySelector('[data-default-sort-by]')?.dataset.defaultSortBy || 'best-selling';
    const sortVal = searchParams.get('sort_by') || defaultSortBy;
    
    const sortingRadios = this.querySelectorAll('input[type="radio"][name="sort_by"]');
    const displayStatusEls = this.querySelectorAll('.sorting-filter .facets__status');
    const sortingSelects = this.querySelectorAll('select[name="sort_by"]');

    sortingRadios.forEach(radio => {
      const isSelected = (radio.value === sortVal);
      radio.checked = isSelected;
      radio.closest('.sorting-filter__option')?.setAttribute('aria-selected', isSelected.toString());
      if (isSelected && radio.dataset.optionName) {
         displayStatusEls.forEach(status => status.textContent = radio.dataset.optionName);
      }
    });

    sortingSelects.forEach(select => {
       select.value = sortVal;
    });

    // Update facet summaries
    const facetInputsComps = this.querySelectorAll('facet-inputs-component, price-facet-component, year-facet-rich-component');
    facetInputsComps.forEach(comp => {
      const details = comp.closest('details');
      const statusComponent = details?.querySelector('facet-status-component');
      if (!statusComponent) return;

      if (comp.tagName.toLowerCase() === 'price-facet-component') {
         const minInput = comp.querySelector('input[name="filter.v.price.gte"]');
         const maxInput = comp.querySelector('input[name="filter.v.price.lte"]');
         if (minInput && maxInput) statusComponent.updatePriceSummary(minInput, maxInput);
      } else if (comp.tagName.toLowerCase() === 'year-facet-rich-component') {
         // Custom year summary logic if needed
      } else {
         if (comp.refs && comp.refs.facetInputs) {
           const checkedInputElements = comp.refs.facetInputs.filter((input) => input.checked);
           statusComponent.updateListSummary(checkedInputElements);
         } else {
           // Fallback for restored panels that might not have component refs initialized yet
           const checkedInputElements = Array.from(comp.querySelectorAll('input[type="checkbox"]:checked'));
           statusComponent.updateListSummary(checkedInputElements);
         }
      }
    });

    this.#syncActiveChips(searchParams);
  }

  /**
   * Generates Active Chips client-side because Shopify hides chips that yield 0 results.
   */
  #syncActiveChips(searchParams) {
    const chipsHTML = [];
    let hasActive = false;
    const yearValues = [];

    // Iterate through searchParams to find active filters
    for (const [key, value] of searchParams.entries()) {
      if (key === 'q' || key === 'sort_by' || key === 'section_id') continue;
      
      if (key === 'filter.v.price.gte' || key === 'filter.v.price.lte') {
         continue; // Handle price separately
      }

      if (key === 'filter.p.m.custom.year') {
         yearValues.push(parseInt(value));
         continue; // Handle year mapping separately
      }

      // Standard list filters (Material, Brand, Model, etc.)
      const input = this.querySelector(`input[name="${key}"][value="${value}"]`);
      if (input) {
         hasActive = true;
         let text = value;
         if (input.dataset.label) {
            text = input.dataset.label;
         } else {
            const explicitLabel = document.querySelector(`label[for="${input.id}"]`);
            if (explicitLabel) {
               const textNode = explicitLabel.querySelector('.checkbox__label-text, .facet-checkbox__text');
               if (textNode) {
                  // Attempt to exclude count if there is a child count span, but taking textContent of parent includes it.
                  // Just take the first child node's text to avoid count, or split.
                  text = Array.from(textNode.childNodes).find(n => n.nodeType === 3)?.textContent.trim() || textNode.textContent.trim();
               } else {
                  text = explicitLabel.textContent.trim();
               }
            } else if (input.closest('label')) {
               text = input.closest('label').textContent.trim();
            }
         }
         
         chipsHTML.push({
            text: text,
            key: key,
            value: value,
            type: 'checkbox'
         });
      }
    }

    // Process Year Grouping into single chip
    if (yearValues.length > 0) {
       hasActive = true;
       const minYear = Math.min(...yearValues);
       const maxYear = Math.max(...yearValues);
       chipsHTML.push({ 
          text: (minYear === maxYear) ? `${minYear}` : `${minYear} - ${maxYear}`, 
          type: 'year' 
       });
    }

    // Price
    const minPrice = searchParams.get('filter.v.price.gte');
    const maxPrice = searchParams.get('filter.v.price.lte');
    if (minPrice || maxPrice) {
       hasActive = true;
       const format = (v) => v ? `Dhs. ${v.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}.00` : (minPrice ? '' : 'Dhs. 0.00');
       let text = `${minPrice ? format(minPrice) : 'Dhs. 0.00'} - ${maxPrice ? format(maxPrice) : 'Max'}`;
       chipsHTML.push({ text: text, type: 'price' });
    }

    // Now inject into DOM
    ['drawer', '', 'top'].forEach(suffix => {
       const wrapperId = suffix ? `#facets-remove-${suffix}-${this.sectionId}` : `#facets-remove-${this.sectionId}`;
       let chipsContainer = document.querySelector(wrapperId);
       
       if (!chipsContainer) return;

       chipsContainer.innerHTML = '';
       
       chipsHTML.forEach(chipData => {
           const btn = document.createElement('button');
           btn.className = "pills__pill pills__pill--desktop-small facets-remove__pill";
           btn.innerHTML = `${chipData.text} <span class="svg-wrapper svg-wrapper--smaller"><svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><path d="M3 3L9 9M9 3L3 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"></path></svg></span>`;
           btn.onclick = (e) => {
              e.preventDefault();
              if (chipData.type === 'checkbox') {
                 const inputs = this.querySelectorAll(`input[name="${chipData.key}"][value="${chipData.value}"]`);
                 inputs.forEach(inp => inp.checked = false);
              } else if (chipData.type === 'year') {
                 this.querySelectorAll('.hidden-checkboxes input[name="filter.p.m.custom.year"]').forEach(inp => inp.checked = false);
                 const yearComps = this.querySelectorAll('year-facet-rich-component');
                 yearComps.forEach(comp => {
                    comp.querySelectorAll('input[type="radio"]').forEach(r => r.checked = false);
                    comp.initFromCheckboxes();
                    const minInput = comp.querySelector('.year-input-min');
                    const maxInput = comp.querySelector('.year-input-max');
                    if (minInput) minInput.value = minInput.min;
                    if (maxInput) maxInput.value = maxInput.max;
                 });
              } else if (chipData.type === 'price') {
                 this.querySelectorAll(`input[name="filter.v.price.gte"], input[name="filter.v.price.lte"]`).forEach(inp => inp.value = '');
                 const priceComps = this.querySelectorAll('price-facet-rich-component');
                 priceComps.forEach(comp => {
                    const radios = comp.querySelectorAll('input[type="radio"]');
                    radios.forEach(r => { r.checked = false; r.closest('label')?.classList.remove('active'); });
                 });
              }
              this.updateFilters();
           };
           chipsContainer.appendChild(btn);
       });

       if (hasActive && suffix !== '') {
          const clearAllBtn = document.createElement('button');
          clearAllBtn.type = 'button';
          clearAllBtn.className = "tw:capitalize button-unstyled facets__clear-all-link tw:block tw:mt-4 tw:mx-auto";
          clearAllBtn.style.color = "gray";
          clearAllBtn.innerHTML = `Clear All`;
          clearAllBtn.onclick = (e) => {
             e.preventDefault();
             window.location.href = window.location.pathname;
          };
          chipsContainer.appendChild(clearAllBtn);
       }
    });

    // Update Bubble Count
    const bubbleTextList = this.querySelectorAll('.filter-count-bubble__text');
    bubbleTextList.forEach(bubble => bubble.textContent = chipsHTML.length);
    const bubbleWrapperList = this.querySelectorAll('.filter-count-bubble');
    bubbleWrapperList.forEach(bubble => {
       if (chipsHTML.length > 0) {
           bubble.style.display = 'flex';
       } else {
           bubble.style.display = 'none';
       }
    });
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   */
  updateFilters() {
    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
  }

  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters();
    }
  }

  /**
   * Handles mouseover events on facet labels
   * @param {MouseEvent} event - The mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = event.target.querySelector('input');

    if (!(inputElement instanceof HTMLInputElement)) return;

    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);

    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    sectionRenderer.getSectionHTML(this.sectionId, true, url);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  /** @type {string} */
  currency;
  /** @type {string} */
  moneyFormat;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
    this.currency = this.dataset.currency ?? 'USD';
    this.moneyFormat = this.#extractMoneyPlaceholder(this.dataset.moneyFormat ?? '{{amount}}');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
  }

  /**
   * Extracts the placeholder from a money format string, removing currency symbols.
   * @param {string} format - The money format (e.g., "${{amount}}", "{{amount}} USD")
   * @returns {string} Just the placeholder (e.g., "{{amount}}")
   */
  #extractMoneyPlaceholder(format) {
    const match = format.match(/{{\s*\w+\s*}}/);
    return match ? match[0] : '{{amount}}';
  }

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#setMinAndMaxValues();
    this.#updateSummary();
  }

  /**
   * Parses a formatted money value into minor units
   * displayValue can come from user input or API response
   * @param {string} displayValue - The display value (e.g., "10.50" for USD, "9,50" for EUR, "1000" for JPY)
   * @param {string} currency - The currency code
   * @returns {number} The value in minor units
   */
  #parseDisplayValue(displayValue, currency) {
    return convertMoneyToMinorUnits(displayValue, currency) ?? 0;
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  #adjustToValidValues(input) {
    if (input.value.trim() === '') return;

    const { currency, moneyFormat } = this;
    // Parse the user's input value using currency-aware parsing
    const value = this.#parseDisplayValue(input.value, currency);

    // data-min and data-max now contain raw minor unit values (not formatted)
    const min = this.#parseDisplayValue(input.getAttribute('data-min') ?? '0', currency);
    const max = this.#parseDisplayValue(input.getAttribute('data-max') ?? '0', currency);

    if (value < min) {
      input.value = formatMoney(min, moneyFormat, currency);
    } else if (value > max) {
      input.value = formatMoney(max, moneyFormat, currency);
    }
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent?.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');
    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
      }
    });

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('facets__clear--active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFiltersByURL(url);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      const activeClass = this.getAttribute('active-class') || 'active';
      clearButton.classList.toggle(activeClass, event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = ['details', 'summary', 'listbox'];

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;
    const isMobile = window.innerWidth < 750;

    const shouldDisable = this.dataset.shouldUseSelectOnMobile === 'true';

    // Because we have a select element on mobile and a bunch of radio buttons on desktop,
    // we need to disable the input during "form-submission" to prevent duplicate entries.
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = true;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = true;
      }
    }

    facetsForm.updateFilters();
    this.updateFacetStatus(event);

    // Re-enable the input after the form-submission
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = false;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = false;
      }
    }

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');
    if (!(facetStatus instanceof FacetStatusComponent)) return;

    facetStatus.textContent =
      event.target.value !== details.dataset.defaultSortBy ? event.target.dataset.optionName ?? '' : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        return swatch?.outerHTML ?? '';
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    const filterStyle = this.dataset.filterStyle;

    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (filterStyle === 'horizontal' && checkedInputElementsCount === 1) {
      facetStatus.innerHTML = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const currency = facetStatus.dataset.currency || '';
    const minInputNum = this.#parseCents(minInputValue, '0', currency);
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax, currency);
    facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as minor units (cents for most currencies, but adjusted for zero-decimal currencies)
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid (formatted string like "11,400")
   * @param {string} currency - The currency code (e.g., 'USD', 'JPY', 'KRW')
   * @returns {number} The money value in minor units
   */
  #parseCents(value, fallback = '0', currency = '') {
    // Try to parse the value
    const result = convertMoneyToMinorUnits(value, currency);
    if (result !== null) return result;

    // Fall back to parsing the fallback string (which may have formatting like "11,400")
    const fallbackResult = convertMoneyToMinorUnits(fallback, currency);
    if (fallbackResult !== null) return fallbackResult;

    // Last resort: clean and parse as integer
    const cleanFallback = fallback.replace(/[^\d]/g, '');
    return parseInt(cleanFallback, 10) || 0;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const format = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return formatMoney(moneyValue, format, currency);
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}
